/**
 * Diagnostic snapshot collector (009). Pulls getStats() off every tapped peer
 * connection and normalizes the fields that actually diagnose call problems:
 * codec, hardware-vs-software encode/decode, qualityLimitationReason, fps,
 * resolution, bitrate (derived from byte deltas), loss, jitter, RTT, freezes.
 * Any missing field is simply omitted — collection never throws.
 */
import { getPeerConnections } from './pcRegistry.js'
import { useRoomStore } from '../room/store.js'
import type { QualityLevel } from '../room/connectionQuality.js'

export interface StreamStat {
  direction: 'send' | 'recv'
  kind: string
  codec?: string | undefined
  implementation?: string | undefined
  scalabilityMode?: string | undefined
  width?: number | undefined
  height?: number | undefined
  fps?: number | undefined
  bitrateKbps?: number | undefined
  packetsLost?: number | undefined
  lossPct?: number | undefined
  jitterMs?: number | undefined
  rttMs?: number | undefined
  qualityLimitationReason?: string | undefined
  freezeCount?: number | undefined
  framesDropped?: number | undefined
  nackCount?: number | undefined
  ssrc?: number | undefined
}

export interface DiagnosticSnapshot {
  tMs: number
  streams: StreamStat[]
  quality: { local: QualityLevel; peers: Record<string, QualityLevel> }
  connectionStatus: string
}

/** Loose view over the RTCStats variants we read. */
interface RtcStatLike {
  type?: string
  id?: string
  kind?: string
  mimeType?: string
  codecId?: string
  ssrc?: number
  timestamp?: number
  // outbound
  bytesSent?: number
  framesEncoded?: number
  framesPerSecond?: number
  frameWidth?: number
  frameHeight?: number
  encoderImplementation?: string
  scalabilityMode?: string
  qualityLimitationReason?: string
  nackCount?: number
  // inbound
  bytesReceived?: number
  framesDecoded?: number
  framesDropped?: number
  freezeCount?: number
  packetsLost?: number
  jitter?: number
  decoderImplementation?: string
  // remote-inbound (for our send streams)
  roundTripTime?: number
  fractionLost?: number
}

interface PrevSample {
  ts: number
  bytes: number
  frames?: number | undefined
}
const prevById = new Map<string, PrevSample>()
const startMs = Date.now()

function round(n: number | undefined, factor = 1): number | undefined {
  return typeof n === 'number' ? Math.round(n * factor) : undefined
}

/**
 * mediasoup probes available bandwidth with a synthetic "probator" RTP stream.
 * It surfaces in getStats() as an `inbound-rtp` with the well-known ssrc 1234
 * and NO codec, and reports bogus huge packetsLost/jitter that would otherwise
 * pollute the diagnostics table and the report (009). Skip such non-media
 * inbound entries: ssrc 1234, OR a video inbound with no associated codec.
 *
 * Pure + exported for a focused unit test.
 */
export function isNonMediaInbound(stat: {
  ssrc?: number
  kind?: string
  codecId?: string
}): boolean {
  if (stat.ssrc === 1234) return true
  // A real video inbound always resolves a codec; the probator does not.
  if ((stat.kind ?? 'video') === 'video' && !stat.codecId) return true
  return false
}

/** Bitrate (kbps) and fps derived from deltas vs the previous snapshot of this stat. */
function deriveRates(
  id: string | undefined,
  ts: number | undefined,
  bytes: number | undefined,
  frames: number | undefined,
): { bitrateKbps?: number | undefined; fps?: number | undefined } {
  if (id === undefined || ts === undefined || bytes === undefined) return {}
  const prev = prevById.get(id)
  prevById.set(id, { ts, bytes, frames })
  if (!prev || ts <= prev.ts) return {}
  const dtSec = (ts - prev.ts) / 1000
  const bitrateKbps = Math.round(((bytes - prev.bytes) * 8) / dtSec / 1000)
  const fps =
    frames !== undefined && prev.frames !== undefined
      ? Math.round((frames - prev.frames) / dtSec)
      : undefined
  return { bitrateKbps, fps }
}

export async function snapshot(): Promise<DiagnosticSnapshot> {
  const streams: StreamStat[] = []

  for (const pc of getPeerConnections()) {
    let report: RTCStatsReport
    try {
      report = await pc.getStats()
    } catch {
      continue
    }

    const codecs = new Map<string, string>()
    const remoteInboundBySsrc = new Map<number, RtcStatLike>()
    for (const value of report.values()) {
      const s = value as RtcStatLike
      if (s.type === 'codec' && s.id && s.mimeType) codecs.set(s.id, s.mimeType)
      else if (s.type === 'remote-inbound-rtp' && s.ssrc !== undefined) remoteInboundBySsrc.set(s.ssrc, s)
    }

    for (const value of report.values()) {
      const s = value as RtcStatLike
      if (s.type === 'outbound-rtp') {
        const rates = deriveRates(s.id, s.timestamp, s.bytesSent, s.framesEncoded)
        const ri = s.ssrc !== undefined ? remoteInboundBySsrc.get(s.ssrc) : undefined
        streams.push({
          direction: 'send',
          kind: s.kind ?? 'video',
          codec: s.codecId ? codecs.get(s.codecId) : undefined,
          implementation: s.encoderImplementation,
          scalabilityMode: s.scalabilityMode,
          width: s.frameWidth,
          height: s.frameHeight,
          fps: s.framesPerSecond ?? rates.fps,
          bitrateKbps: rates.bitrateKbps,
          qualityLimitationReason: s.qualityLimitationReason,
          nackCount: s.nackCount,
          rttMs: round(ri?.roundTripTime, 1000),
          lossPct: ri ? round(ri.fractionLost, 100) : undefined,
          jitterMs: round(ri?.jitter, 1000),
          ssrc: s.ssrc,
        })
      } else if (s.type === 'inbound-rtp') {
        // Skip mediasoup's bandwidth-probation "probator" stream (ssrc 1234 /
        // codec-less video) — it reports bogus loss/jitter, not real media.
        if (isNonMediaInbound(s)) continue
        const rates = deriveRates(s.id, s.timestamp, s.bytesReceived, s.framesDecoded)
        streams.push({
          direction: 'recv',
          kind: s.kind ?? 'video',
          codec: s.codecId ? codecs.get(s.codecId) : undefined,
          implementation: s.decoderImplementation,
          width: s.frameWidth,
          height: s.frameHeight,
          fps: s.framesPerSecond ?? rates.fps,
          bitrateKbps: rates.bitrateKbps,
          packetsLost: s.packetsLost,
          jitterMs: round(s.jitter, 1000),
          freezeCount: s.freezeCount,
          framesDropped: s.framesDropped,
          ssrc: s.ssrc,
        })
      }
    }
  }

  const st = useRoomStore.getState()
  return {
    tMs: Date.now() - startMs,
    streams,
    quality: { local: st.localQuality, peers: { ...st.peerQuality } },
    connectionStatus: st.connectionStatus,
  }
}
